const ALLOWED_TAGS = new Set([
  "B",
  "BR",
  "EM",
  "I",
  "LI",
  "OL",
  "P",
  "STRONG",
  "U",
  "UL"
]);

const BLOCKED_TAGS = new Set([
  "EMBED",
  "IFRAME",
  "MATH",
  "OBJECT",
  "SCRIPT",
  "STYLE",
  "SVG"
]);

const escapeHtml = (html: string) =>
  html
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const sanitizeNode = (node: Node): Node | DocumentFragment | null => {
  if (node.nodeType === Node.TEXT_NODE) {
    return document.createTextNode(node.textContent ?? "");
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const element = node as HTMLElement;

  if (BLOCKED_TAGS.has(element.tagName)) {
    return null;
  }

  const sanitizedChildren = Array.from(element.childNodes)
    .map(sanitizeNode)
    .filter((child): child is Node | DocumentFragment => child !== null);

  if (!ALLOWED_TAGS.has(element.tagName)) {
    const fragment = document.createDocumentFragment();
    sanitizedChildren.forEach((child) => fragment.appendChild(child));

    return fragment;
  }

  const sanitizedElement = document.createElement(element.tagName.toLowerCase());
  sanitizedChildren.forEach((child) => sanitizedElement.appendChild(child));

  return sanitizedElement;
};

export const sanitizeHtml = (html: string | null | undefined) => {
  if (!html) return "";

  if (typeof DOMParser === "undefined" || typeof document === "undefined") {
    return escapeHtml(html);
  }

  const parsedDocument = new DOMParser().parseFromString(html, "text/html");
  const fragment = document.createDocumentFragment();

  Array.from(parsedDocument.body.childNodes)
    .map(sanitizeNode)
    .filter((child): child is Node | DocumentFragment => child !== null)
    .forEach((child) => fragment.appendChild(child));

  const container = document.createElement("div");
  container.appendChild(fragment);

  return container.innerHTML;
};
